import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/client.jsx';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { usersFixture, userDetailFixture, userTenantsFixture } from './fixture';

/**
 * Server-side LIKE-search + status filter.
 * Spec: GET /v1/admin/users?q=&status=
 */
export function useUsers({ search = '', status = '' } = {}) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();

  return useQuery({
    queryKey: ['users', { search, status }, fixturesMode ? 'fixture' : 'live'],
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay();
        return usersFixture({ search, status });
      }
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (status) params.set('status', status);
      const qs = params.toString();
      return fetch(`/v1/admin/users${qs ? `?${qs}` : ''}`);
    },
    staleTime: 30_000,
  });
}

/** GET /v1/admin/users/:id */
export function useUserDetail(userId) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();

  return useQuery({
    queryKey: ['user', userId, fixturesMode ? 'fixture' : 'live'],
    enabled: !!userId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay(150);
        const data = userDetailFixture(userId);
        if (!data) throw new Error('user_not_found_in_tenant');
        return data;
      }
      return fetch(`/v1/admin/users/${userId}`);
    },
  });
}

/** GET /v1/admin/users/:id/tenants — Memberships für JIT-Operator */
export function useUserTenants(userId) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();

  return useQuery({
    queryKey: ['userTenants', userId, fixturesMode ? 'fixture' : 'live'],
    enabled: !!userId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay(100);
        return userTenantsFixture(userId);
      }
      return fetch(`/v1/admin/users/${userId}/tenants`);
    },
  });
}

/**
 * Business-Accounts (Spec: lmm-wiki/wiki/orbit-specs/business-accounts-admin.md).
 *
 * Drei Read-Only-Endpoints — nur sichtbar im Detail-Drawer wenn der User
 * eine Business-Org ist (`accountType === 'business' && parent_user_id === null`).
 *
 * Backend liefert (Migration 022 — Stand 2026-06-14 pending in CORE).
 * 404 wird tolerant behandelt: leere Liste statt Page-Crash.
 */

/** GET /v1/admin/users/:id/sub-users */
export function useUserSubUsers(userId, { enabled = true } = {}) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['userSubUsers', userId, fixturesMode ? 'fixture' : 'live'],
    enabled: enabled && !!userId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay(100);
        return { users: [], unavailable: true };
      }
      try {
        const res = await fetch(`/v1/admin/users/${userId}/sub-users`);
        const list = res?.users ?? res?.items ?? (Array.isArray(res) ? res : []);
        return { users: list };
      } catch (err) {
        if (err?.status === 404) return { users: [], unavailable: true };
        throw err;
      }
    },
  });
}

/** GET /v1/admin/users/:id/contacts */
export function useUserContacts(userId, { enabled = true } = {}) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['userContacts', userId, fixturesMode ? 'fixture' : 'live'],
    enabled: enabled && !!userId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay(100);
        return { contacts: [], unavailable: true };
      }
      try {
        const res = await fetch(`/v1/admin/users/${userId}/contacts`);
        const list = res?.contacts ?? res?.items ?? (Array.isArray(res) ? res : []);
        return { contacts: list };
      } catch (err) {
        if (err?.status === 404) return { contacts: [], unavailable: true };
        throw err;
      }
    },
  });
}

/** GET /v1/admin/users/:id/communication */
export function useUserCommunication(userId, { enabled = true } = {}) {
  const { fetch } = useApiClient();
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['userCommunication', userId, fixturesMode ? 'fixture' : 'live'],
    enabled: enabled && !!userId,
    queryFn: async () => {
      if (fixturesMode) {
        await fixtureDelay(100);
        return { channels: [], unavailable: true };
      }
      try {
        const res = await fetch(`/v1/admin/users/${userId}/communication`);
        const list = res?.channels ?? res?.items ?? (Array.isArray(res) ? res : []);
        return { channels: list };
      } catch (err) {
        if (err?.status === 404) return { channels: [], unavailable: true };
        throw err;
      }
    },
  });
}
